import { ApiError } from "@/lib/api";

/**
 * Why a person Clerk has signed in cannot get into GeoKerja (BUG-2026-018,
 * BR-014), in the three forms worth telling them apart:
 *
 *  - `not-linked` — no GeoKerja record for this email. GeoKerja accounts are
 *    invitation-only, so this is someone who signed up on their own (the
 *    production Clerk instance still offered "Sign up") or signed in with a
 *    different email from the one they were invited with.
 *  - `inactive` — a record exists, and the organisation has deactivated it.
 *  - `unavailable` — the account could not be loaded (network, server, an
 *    expired session). Nothing is concluded about the account; trying again
 *    may work.
 */
export type AccountProblemKind = "not-linked" | "inactive" | "unavailable";

export interface AccountProblem {
  kind: AccountProblemKind;
  /** The server's own words — for the console, not the page. */
  message: string;
}

/**
 * Read one refusal. The guard's refusals carry no code, so they are told apart
 * by their wording (`common/guards/clerk-auth.guard.ts`): "No … account linked
 * to this Clerk user" and "… account is not active". Anything else — a network
 * failure, a server error, an expired session — concludes nothing.
 */
function kindOf(error: unknown): AccountProblemKind {
  if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403)) {
    return "unavailable";
  }
  if (/not active/i.test(error.message)) return "inactive";
  if (/linked/i.test(error.message)) return "not-linked";
  return "unavailable";
}

/**
 * Both lookups refused — the admin one and the employee one. A deactivated
 * record on either side is the more specific answer (the person does have an
 * account); a lookup that failed for another reason means nothing can be
 * concluded; only when both find no record is there no account.
 */
export function accountProblem(adminError: unknown, employeeError: unknown): AccountProblem {
  const kinds = [kindOf(adminError), kindOf(employeeError)];
  const kind: AccountProblemKind = kinds.includes("inactive")
    ? "inactive"
    : kinds.includes("unavailable")
      ? "unavailable"
      : "not-linked";
  const first = adminError ?? employeeError;
  const message =
    first instanceof Error ? first.message : "Could not load your profile";
  return { kind, message };
}
